import type { ReactNode } from 'react';
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { getSortableMove, useSortableSensors } from './sortable-utils';

export interface SortableListProps {
  /** Sortable ids in render order; must match the `SortableItem` ids. */
  items: UniqueIdentifier[];
  /** Called with the original and final index after a successful reorder. */
  onMove: (fromIndex: number, toIndex: number) => void;
  children: ReactNode;
}

/**
 * Single vertical sortable list (DndContext + SortableContext) for legacy
 * screens. Renders no DOM of its own, so it can wrap a `<tbody>` or `<ol>`.
 */
export const SortableList = ({ items, onMove, children }: SortableListProps) => {
  const sensors = useSortableSensors();

  const handleDragEnd = (event: DragEndEvent) => {
    const move = getSortableMove(event);
    if (move) {
      onMove(move.from, move.to);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
};
